import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import RecipientPicker from './RecipientPicker';
import CircularProgress from '@mui/material/CircularProgress';
import { ArrowLeft, Check, Users } from 'lucide-react';

// Presentational: layout only, all state lives in GroupComposerContainer.
// Mirrors BroadcastComposer's structure, but a group chat also needs a name
// and is bidirectional (no confirm-step — creation is not a one-shot send
// to up to 40 recipients, membership can be edited later).
const GroupComposer = ({
  onBack,
  name,
  onNameChange,
  students,
  recipientsLoading,
  selectedIds,
  onToggleRecipient,
  onSelectAll,
  onClearAll,
  maxMembers,
  creating,
  error,
  onCreate,
}) => {
  const theme = useTheme();
  const isDark = theme?.mode === 'dark';
  const accentColor = theme?.colors?.button_light || '#fdbf00';
  const primaryColor = theme?.colors?.primary_light || '#1a4275';
  const borderCol = isDark ? 'rgba(51, 65, 85, 0.4)' : '#e2e8f0';
  const textPrimary = isDark ? '#f8fafc' : '#1e293b';

  const overCap = selectedIds.length > maxMembers;
  const canCreate = name.trim().length > 0 && selectedIds.length > 0 && !overCap && !creating;

  return (
    <div className="w-full flex flex-col h-full overflow-hidden relative">
      {/* Header */}
      <div
        className="p-4 flex items-center gap-3.5 border-b shadow-sm transition-colors duration-300"
        style={{ borderColor: borderCol, backgroundColor: isDark ? '#162542' : '#ffffff' }}
      >
        <button
          onClick={onBack}
          className="p-2 rounded-full hover:bg-slate-100/80 dark:hover:bg-slate-800/50 transition-colors"
          style={{ color: accentColor }}
        >
          <ArrowLeft size={22} />
        </button>
        <div className="flex items-center gap-2">
          <Users size={20} style={{ color: accentColor }} />
          <h3 className="font-bold text-xl m-0" style={{ color: textPrimary }}>
            Nuevo grupo
          </h3>
        </div>
      </div>

      {error && (
        <div className="bg-rose-500/10 text-rose-500 p-2.5 text-base font-medium text-center border-b border-rose-500/20">
          {error}
        </div>
      )}

      {/* Group name */}
      <div className="px-4 pt-3 pb-1">
        <input
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Nombre del grupo..."
          disabled={creating}
          className={`w-full rounded-full px-4 py-2.5 text-lg font-medium focus:outline-none transition-all duration-300 ${
            isDark
              ? 'bg-slate-800/80 text-white placeholder-slate-500 focus:bg-slate-800 focus:ring-1 focus:ring-slate-600'
              : 'bg-slate-100 text-slate-800 placeholder-slate-400 focus:bg-white focus:ring-1 focus:ring-slate-300'
          }`}
        />
      </div>

      {/* Members */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <RecipientPicker
          students={students}
          selectedIds={selectedIds}
          onToggle={onToggleRecipient}
          onSelectAll={onSelectAll}
          onClearAll={onClearAll}
          maxRecipients={maxMembers}
          loading={recipientsLoading}
        />
      </div>

      {/* Create button */}
      <div className="p-3 border-t transition-colors duration-300" style={{ backgroundColor: isDark ? '#111e35' : '#f8fafc', borderColor: borderCol }}>
        <button
          type="button"
          disabled={!canCreate}
          onClick={onCreate}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-lg text-white disabled:opacity-40 transition-colors"
          style={{ backgroundColor: primaryColor }}
        >
          {creating ? <CircularProgress size={18} color="inherit" /> : <Check size={18} />}
          Crear grupo
        </button>
      </div>
    </div>
  );
};

export default GroupComposer;
